import React, { useState, useMemo, useEffect } from 'react'
import { makeStyles, Box, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { styles } from './PrintersModalStyles'
import CancelIcon from 'Assets/Images/icons/cancel'
import papaParse from 'papaparse'
import { GenericButton } from '../../../../../../Components/Buttons/GenericButton'
import {
  DMDocumentFormats,
  StatesRegions,
} from '../../../../../../shared/constants'
import { downloadStringAsFile } from '../../../../../../Utils/Misc'
import Input from '../../../../../../Components/Inputs/Input'
import Select from '../../../../../../Components/Inputs/Select'
import { createPrinter, updatePrinter } from '../../../../../../Module/graphQL'
import { updatePrinterCosts } from '../../../../gql'
import { authorization } from '../../../../../../Module/Auth/Auth'
import {
  validateBmeuZipcode,
  validateMailerId,
} from '../../../../../../Utils/Validators/formValidation'

const useStyles = makeStyles(() => styles)

const latitudeLongitudeRanges = {
  latitude: { min: -90, max: 90 },
  longitude: { min: -180, max: 180 },
}

export const PrintersModal = ({
  setShowModal,
  statesData,
  printer,
  countryId,
  refetch,
}) => {
  const user = authorization.getUser()
  const isEdit = !!printer
  const classes = useStyles()
  const [uploadedFile, setUploadedFile] = useState(null)
  const [fileValidationError, setFileValidationError] = useState('')
  const [parsedUploadedFile, setParsedUploadedFile] = useState(null)
  const [disabled, setDisabled] = useState(true)
  const [fields, setFields] = useState({
    printerName: { value: '', error: true },
    email: { value: '', error: true, additionalError: false },
    phone: { value: '', error: true },
    address: { value: '', error: true },
    crid: { value: '', error: false },
    permit: { value: '', error: false },
    city: { value: '', error: true },
    zipcode: { value: '', error: true },
    bmeuZipcode: { value: '', error: false, additionalError: '' },
    state: { value: [], error: true },
    firstName: { value: '', error: false },
    lastName: { value: '', error: false },
    latitude: { value: '', error: true },
    longitude: { value: '', error: true },
    availableStates: { value: [], error: true },
    regionAvailable: { value: [], error: true },
    mailerId: { value: '', error: false },
  })

  useEffect(() => {
    if (printer) {
      setFields(prev => {
        return {
          ...prev,
          printerName: { value: printer.name, error: !printer.name },
          email: {
            value: printer.email,
            error: !printer.email,
            additionalError: false,
          },
          phone: { value: printer.phoneNumber, error: !printer.phoneNumber },
          address: { value: printer.address, error: !printer.address },
          crid: { value: printer.crid, error: false },
          mailerId: { value: printer.mailerId, error: false },
          permit: { value: printer.permit, error: false },
          city: { value: printer.city, error: !printer.city },
          zipcode: { value: printer.zipcode, error: !printer.zipcode },
          bmeuZipcode: { value: printer.bmeuZipcode, error: false },
          state: { value: String(printer.stateId), error: !printer.stateId },
          firstName: { value: printer.firstName, error: false },
          lastName: { value: printer.lastName, error: false },
          latitude: {
            value: printer.latitude || '',
            error: !printer.latitude,
            // additionalError: !printer.latitude ? 'Invalid latitude' : false,
          },
          longitude: {
            value: printer.longitude || '',
            error: !printer.longitude,
            // additionalError: !printer.longitude ? 'Invalid latitude' : false,
          },
          availableStates: {
            value: printer.availableStates.map(id => String(id)),
            error: !printer.availableStates || !printer.availableStates.length,
          },
        }
      })
    }
  }, [printer])

  useEffect(() => {
    const fieldsHasError = Object.values(fields).some(
      ({ error, additionalError }) => error || additionalError,
    )

    let disabled = true

    if (fieldsHasError) {
      disabled = true
    } else {
      disabled = false
    }

    if (fileValidationError) {
      disabled = true
    }

    setDisabled(disabled)
  }, [fields, fileValidationError, isEdit, uploadedFile])

  useEffect(() => {
    if (fields.availableStates.value.length) {
      setFields(prev => ({
        ...prev,
        regionAvailable: {
          value: [
            ...new Set(
              fields.availableStates.value
                .map(
                  stateId =>
                    StatesRegions[
                      statesData.find(s => s.id == stateId).region.toLowerCase()
                    ],
                )
                .filter(r => !!r),
            ),
          ],
        },
      }))
    } else if (!fields.availableStates.value.length) {
      setFields(prev => ({
        ...prev,
        regionAvailable: {
          value: [],
        },
      }))
    }
  }, [fields.availableStates.value])

  const onChange = type => (value, error) => {
    if (type === 'latitude' || type === 'longitude') {
      const newValue = value.replace(/[^0-9-.]/gi, '')
      const latLongPattern = /^-?(\d{1,2}(\.\d*)?|1[0-7]\d(\.\d*)?|180(\.0*)?)$/

      if (latLongPattern.test(newValue)) {
        const numValue = parseFloat(newValue)
        const isOnRange =
          numValue >= latitudeLongitudeRanges[type].min &&
          numValue <= latitudeLongitudeRanges[type].max
        setFields(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            value: newValue,
            error: isOnRange ? !!error : true,
            additionalError: isOnRange ? '' : `Invalid ${type}`,
          },
        }))
      } else {
        setFields(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            value: newValue,
            error: true,
            additionalError: `Invalid ${type}`,
          },
        }))
      }
    } else if (type === 'bmeuZipcode') {
      const bmeuZipcodeError = !validateBmeuZipcode(value)

      setFields(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          value,
          error: !!value && bmeuZipcodeError ? true : false,
          additionalError:
            !!value && bmeuZipcodeError
              ? 'BMEU zip code is invalid. Please, enter the correct zip code'
              : '',
        },
      }))
    } else if (type === 'mailerId') {
      const mailerIdError = !validateMailerId(value)
      setFields(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          value,
          error: !!value && mailerIdError ? true : false,
          additionalError:
            !!value && mailerIdError
              ? 'Invalid mailer ID. Should have 6 or 9 digits.'
              : '',
        },
      }))
    } else {
      setFields(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          value,
          error: !!error,
        },
      }))
    }
  }

  const onSubmit = async () => {
    try {
      if (!disabled) {
        const printerPayload = {
          countryId: Number(countryId),
          name: fields.printerName.value,
          stateId: Number(fields.state.value),
          address: fields.address.value,
          zipcode: fields.zipcode.value,
          bmeuZipcode: fields.bmeuZipcode.value || '',
          city: fields.city.value,
          firstName: fields.firstName.value || '',
          lastName: fields.lastName.value || '',
          phoneNumber: fields.phone.value,
          email: fields.email.value,
          updatedBy: Number(user.id),
          permit: fields.permit.value || '',
          crid: fields.crid.value || '',
          latitude: Number(fields.latitude.value),
          longitude: Number(fields.longitude.value),
          mailerId: fields.mailerId.value
            ? Number(fields.mailerId.value)
            : null,
          availableStates: fields.availableStates.value.map(stateId =>
            Number(stateId),
          ),
        }
        let response
        let printerId
        if (!isEdit) {
          response = await createPrinter(printerPayload)
          const {
            data: {
              createPrinter: {
                Printer: { id },
              },
            },
          } = await response.json()
          printerId = id
        } else {
          response = await updatePrinter({ ...printerPayload, id: printer.id })
          const {
            data: {
              updatePrinter: {
                Printer: { id },
              },
            },
          } = await response.json()
          printerId = id
        }

        if (
          uploadedFile &&
          parsedUploadedFile &&
          printerId &&
          !fileValidationError
        ) {
          const response = await updatePrinterCosts({
            NewPrinterCosts: parsedUploadedFile.map(row => ({
              ...row,
              printerId,
            })),
          })
          const {
            data: {
              updatePrinterCosts: { error },
            },
          } = await response.json()

          if (error) {
            throw new Error(error)
          }
        }

        refetch()
      }
    } catch (e) {
      setUploadedFile(null)
      setFileValidationError('')
      setParsedUploadedFile(null)
      setShowModal(false)
    }
  }

  useEffect(() => {
    if (uploadedFile) {
      parseFile()
    } else {
      setParsedUploadedFile(null)
    }
  }, [uploadedFile])

  const parseFile = async () => {
    await papaParse.parse(uploadedFile, {
      complete: results => {
        const columns = results.data[0].filter(header => header)
        const dataForSaving = columns
          .slice(1) // remove flyer count
          .reduce((acc, column, index) => {
            const filteredPrintingCostsData = results.data
              .slice(1) // remove headers, keep only values
              .filter(data => data[0])
            if (column.includes('FREIGHT')) {
              return acc
            }
            return [
              ...acc,
              ...filteredPrintingCostsData.reduce(
                (accumulator, printingCostsData) => {
                  if (!printingCostsData.length || !printingCostsData[0])
                    return acc
                  return [
                    ...accumulator,
                    {
                      label: column,
                      value: Number(
                        Number(printingCostsData[index + 1]).toFixed(4),
                      ),
                      flyersCount: Number(printingCostsData[0]),
                      updatedBy: Number(user.id),
                      freight: Number(printingCostsData[index + 2]),
                    },
                  ]
                },
                [],
              ),
            ]
          }, [])
        setParsedUploadedFile(dataForSaving)
      },
    })
  }

  const validateUploadedFile = async file => {
    if (!file) {
      return setFileValidationError('No file uploaded')
    }
    if (file.type !== 'text/csv') {
      return setFileValidationError('Wrong file type. CSV required.')
    }

    papaParse.parse(file, {
      complete: result => {
        const { data } = result
        while (data.length) {
          const lastRow = data.pop()
          if (lastRow.findIndex(item => item) !== -1) {
            data.push(lastRow)
            break
          }
        }

        const headers = [...data[0]].filter(header => header)
        const values = data.filter(
          (data, index) => index !== 0 && data.every(value => value),
        )

        if (values.length < data.length - 1 || values.length === 0) {
          return setFileValidationError(
            'Wrong file data. Empty values are not acceptable.',
          )
        }

        if (headers[0] !== 'FLYERS_COUNT') {
          return setFileValidationError(
            'There is an error in FLYERS_COUNT header.',
          )
        }

        let freightError = headers.reduce((error, header, index) => {
          if (
            !!index &&
            index % 2 === 0 &&
            header &&
            !header.toLowerCase().includes('_freight')
          ) {
            error = `There is no freight cost for ${headers[index - 1]}.`
            return error
          }
          return error
        }, '')

        const lastHeader = headers[headers.length - 1]
        if (!freightError && !lastHeader.toLowerCase().includes('_freight')) {
          freightError = `There is no freight cost for ${lastHeader}.`
        }

        if (freightError) {
          return setFileValidationError(freightError)
        }

        const flyersCountObj = values.reduce((acc, value) => {
          return {
            ...acc,
            [value[0]]: acc[value[0]] ? acc[value[0]] + 1 : 1,
          }
        }, {})

        const isDuplicatesFlyersCount = !Object.values(flyersCountObj).every(
          count => count === 1,
        )

        if (isDuplicatesFlyersCount) {
          return setFileValidationError(
            `There are duplicates flyers count in the file.`,
          )
        }

        const isInvalidFlyerFormatValues = values.some(flyerFormatValues => {
          const isInvalidValues = flyerFormatValues.some((value, index) => {
            if (index === 0 && !/^[1-9]+\d+$/.test(value)) {
              setFileValidationError(
                `Wrong FLYERS_COUNT value ${value}. Only an integer value is allowed.`,
              )
              return true
            }
            if (index !== 0 && (isNaN(value) || Number(value) <= 0)) {
              setFileValidationError(
                `Wrong value ${value}. Printing cost can only include numbers and one dot.`,
              )
              return true
            }

            return false
          })

          if (isInvalidValues) {
            return true
          } else {
            return false
          }
        })

        if (isInvalidFlyerFormatValues) return
      },
    })
  }

  const handleFileUpload = async files => {
    if (files[0]) {
      validateUploadedFile(files[0])
      setUploadedFile(files[0])
    } else {
      setUploadedFile(null)
      setFileValidationError('')
    }
  }

  const stateOptions = useMemo(
    () =>
      statesData
        ? statesData
            .sort((a, b) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            )
            .map(s => ({ value: s.id, name: `${s.code} - ${s.name}` }))
        : [],
    [statesData],
  )

  const handleTemplateDownload = () => {
    const headers = [
      'FLYERS_COUNT',
      DMDocumentFormats.SMALL,
      `${DMDocumentFormats.SMALL}_FREIGHT`,
      DMDocumentFormats.BEST_SELLER,
      `${DMDocumentFormats.BEST_SELLER}_FREIGHT`,
      DMDocumentFormats.LARGE,
      `${DMDocumentFormats.LARGE}_FREIGHT`,
      DMDocumentFormats.JUMBO,
      `${DMDocumentFormats.JUMBO}_FREIGHT`,
      DMDocumentFormats.OVERSIZED,
      `${DMDocumentFormats.OVERSIZED}_FREIGHT`,
    ]
    const printingCostsTemplate =
      headers.join(',') +
      '\n' +
      headers.map(() => '').join(',') +
      '\n' +
      headers.map(() => '').join(',')
    downloadStringAsFile(printingCostsTemplate, 'PrinterCosts.csv', 'text/csv')
  }

  return (
    <div className={classes.modalContainer}>
      <div className={classes.closeContainer}>
        <CloseIcon
          fontSize="small"
          onClick={() => setShowModal(false)}
          className={classes.closeButton}
        />
      </div>
      <div className={classes.titleContainer}>
        <span className={classes.title}>
          {isEdit ? 'Edit' : 'Add new'} printer
        </span>
      </div>
      <div className={classes.formContainer}>
        <form id="sign-up-form" className="content-inner" onSubmit={onSubmit}>
          <Input
            label="Printer name"
            value={fields.printerName.value}
            onChange={onChange('printerName')}
            required
          />
          <div className={classes.flex}>
            <Input
              label="Address"
              value={fields.address.value}
              onChange={onChange('address')}
              required
            />
            <Input
              className={classes.ml10}
              label="City"
              value={fields.city.value}
              onChange={onChange('city')}
              required
              onlyString
            />
          </div>
          <div className={classes.flex}>
            <Input
              label="Zipcode"
              value={fields.zipcode.value}
              onChange={onChange('zipcode')}
              required
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
              className={classes.ml10}
            >
              <Input
                label="BMEU zipcode"
                value={fields.bmeuZipcode.value}
                placeholder="BMEU zipcode + 4 Code"
                additionalError={fields.bmeuZipcode.additionalError}
                onChange={onChange('bmeuZipcode')}
              />
            </div>
          </div>
          <div className={classes.flex}>
            <Input
              label={'Latitude'}
              value={fields.latitude.value}
              additionalError={fields.latitude.additionalError}
              required
              onChange={onChange('latitude')}
            />
            <Input
              className={classes.ml10}
              label="Longitude"
              value={fields.longitude.value}
              additionalError={fields.longitude.additionalError}
              required
              onChange={onChange('longitude')}
            />
          </div>
          <div className={classes.flex}>
            <Input
              label="First Name"
              value={fields.firstName.value}
              onChange={onChange('firstName')}
              onlyString
            />
            <Input
              className={classes.ml10}
              label="Last Name"
              value={fields.lastName.value}
              onChange={onChange('lastName')}
              onlyString
            />
          </div>
          <div className={classes.flex}>
            <Input
              label="Email address"
              value={fields.email.value}
              additionalError={fields.email.additionalError}
              onChange={onChange('email')}
              required
              email
            />
            <Input
              label="Phone"
              className={classes.ml10}
              value={fields.phone.value}
              onChange={onChange('phone')}
              required
              cypress_id={'phoneInput'}
            />
          </div>
          <Select
            label="State"
            placeholder="State"
            value={fields.state.value}
            onChange={onChange('state')}
            options={stateOptions}
            MenuProps={{
              getContentAnchorEl: () => null,
            }}
          />
          <div className={classes.flex}>
            <Input
              label="CRID"
              value={fields.crid.value}
              onChange={onChange('crid')}
            />
            <Input
              label="Permit number"
              className={classes.ml10}
              value={fields.permit.value}
              onChange={onChange('permit')}
            />
          </div>
          <div className={classes.flex}>
            <Input
              label="Mailer ID"
              value={fields.mailerId.value}
              placeholder="6 or 9 digits number"
              additionalError={fields.mailerId.additionalError}
              onChange={onChange('mailerId')}
            />
          </div>
          <Select
            label="Available states"
            placeholder="Available states"
            value={fields.availableStates.value}
            onChange={onChange('availableStates')}
            options={stateOptions}
            multiple
            MenuProps={{
              getContentAnchorEl: () => null,
            }}
          />

          <Select
            label="Regions available"
            value={fields.regionAvailable.value}
            onChange={() => {}}
            options={Object.values(StatesRegions).map(region => ({
              value: region,
              name: region,
            }))}
            disabled
            multiple
          />

          {isEdit && (
            <Typography className={classes.uploadWarn}>
              Not uploading a new .csv keeps the existing one, uploading a new
              one overwrites it.
            </Typography>
          )}

          <Box
            className={classes.uploadWrapper}
            onDrop={e => {
              e.preventDefault()
              e.stopPropagation()

              if (!uploadedFile) {
                handleFileUpload(e.dataTransfer.files)
              }
            }}
            onDragOver={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <input
              style={{ display: 'none' }}
              id="upload-printer-costs"
              type="file"
              onChange={e => {
                if (!uploadedFile) {
                  handleFileUpload(e.target.files)
                }
                e.target.value = null
                return
              }}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
            <label
              htmlFor="upload-printer-costs"
              style={{
                width: '100%',
                cursor: 'pointer',
              }}
            >
              <Box className={classes.upload}>
                <svg
                  width="40"
                  height="30"
                  viewBox="0 0 40 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M32.25 11.325C31.1167 4.85625 26.0667 0 20 0C15.1833 0 11 3.075 8.91667 7.575C3.9 8.175 0 12.9563 0 18.75C0 24.9562 4.48333 30 10 30H31.6667C36.2667 30 40 25.8 40 20.625C40 15.675 36.5833 11.6625 32.25 11.325ZM23.3333 16.875V24.375H16.6667V16.875H11.6667L20 7.5L28.3333 16.875H23.3333Z"
                    fill="#2E3860"
                  />
                </svg>

                <Typography className={classes.uploadTitle}>
                  Drop your file here
                </Typography>
                <Typography className={classes.uploadMessage}>
                  Only excel and csv format accepted
                </Typography>
              </Box>
            </label>

            {uploadedFile && (
              <Box className={classes.uploaded}>
                <Typography>{uploadedFile.name}</Typography>
                <CancelIcon
                  onClick={() => {
                    setUploadedFile(null)
                    setFileValidationError('')
                  }}
                />
              </Box>
            )}
          </Box>

          {fileValidationError && (
            <Typography className={classes.fileValidationError}>
              Error! {fileValidationError}
            </Typography>
          )}

          <Box className={classes.downloadTemplateWrapper}>
            <svg
              width="41"
              height="48"
              viewBox="0 0 41 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '13px', width: '70px' }}
            >
              <path
                d="M40.6467 5.59467H37.0414V0.952148H9.3262L0 10.4924V47.0481H37.0414V17.1187H40.6467V5.59467ZM8.84877 3.79199V8.08879C8.84877 9.18357 7.61406 10.0067 6.58513 10.0067H2.78222L8.84877 3.79199ZM35.3951 45.4018H1.64628V11.653H6.58513C8.4372 11.653 10.4951 10.1878 10.4951 8.08879V2.59843H35.3951V5.59467H14.3062V17.1187H35.3951V45.4018ZM33.9217 14.18H32.1602L30.1105 8.41805H31.6086L33.0409 12.7807L34.4814 8.41805H35.9713L33.9217 14.18ZM28.0691 12.2127C27.9658 12.1155 27.8459 12.0374 27.7152 11.9823C27.5191 11.9019 27.3185 11.8332 27.1143 11.7765C26.8018 11.6834 26.4965 11.5678 26.2006 11.4307C25.9618 11.3085 25.7536 11.1341 25.5915 10.9204C25.4063 10.6531 25.3164 10.3313 25.3363 10.0067C25.3263 9.76422 25.374 9.52281 25.4757 9.30241C25.5773 9.08201 25.7298 8.88892 25.9207 8.73907C26.3767 8.44357 26.9084 8.28633 27.4518 8.28633C27.9951 8.28633 28.5269 8.44357 28.9828 8.73907C29.1847 8.89244 29.3484 9.09034 29.4612 9.31736C29.5739 9.54437 29.6328 9.79437 29.6331 10.0479H28.0856C28.0861 9.96321 28.0679 9.87949 28.0323 9.80266C27.9967 9.72584 27.9447 9.65779 27.8798 9.60337C27.7426 9.49338 27.5698 9.43771 27.3942 9.44697C27.2456 9.43986 27.0994 9.48663 26.9826 9.57868C26.9297 9.62943 26.8883 9.691 26.8613 9.75918C26.8343 9.82736 26.8223 9.90056 26.8262 9.97378C26.8263 10.0636 26.8509 10.1516 26.8973 10.2285C26.9437 10.3054 27.0102 10.3681 27.0896 10.41C27.3523 10.5491 27.628 10.6621 27.9127 10.7475C28.2251 10.8475 28.5304 10.9685 28.8264 11.1097C29.0642 11.2263 29.2703 11.3986 29.4273 11.6118C29.6011 11.8504 29.6909 12.1399 29.6825 12.435C29.6859 12.7513 29.6003 13.0622 29.4356 13.3322C29.2569 13.6111 29.0029 13.8337 28.703 13.9743C28.3511 14.1417 27.9647 14.2235 27.5753 14.213C27.1867 14.2202 26.8006 14.1502 26.4393 14.0072C26.1131 13.8855 25.8279 13.6744 25.6162 13.3981C25.4272 13.1274 25.324 12.8062 25.3198 12.4761H26.818C26.82 12.567 26.8404 12.6564 26.8778 12.7392C26.9153 12.8219 26.969 12.8963 27.0359 12.9578C27.1028 13.0193 27.1814 13.0666 27.267 13.097C27.3526 13.1274 27.4434 13.1402 27.5341 13.1346C27.7112 13.146 27.8864 13.0934 28.028 12.9865C28.0889 12.9411 28.1374 12.8811 28.169 12.8121C28.2007 12.743 28.2145 12.6671 28.2091 12.5914C28.2137 12.522 28.2037 12.4524 28.1796 12.3871C28.1554 12.3219 28.1178 12.2624 28.0691 12.2127ZM20.809 12.4761C20.9406 12.6243 21.1033 12.7417 21.2854 12.8199C21.4675 12.8982 21.6646 12.9354 21.8626 12.9289C22.1167 12.9319 22.3669 12.8665 22.587 12.7395C22.7995 12.6073 22.9684 12.4155 23.0727 12.188H24.6202C24.4812 12.7809 24.1335 13.3039 23.6406 13.6615C23.1296 14.0323 22.5102 14.2233 21.8791 14.2047C21.3548 14.2156 20.8372 14.0848 20.381 13.8261C19.9458 13.5907 19.5861 13.2368 19.3438 12.8054C19.0914 12.3381 18.9638 11.8136 18.9734 11.2826C18.965 10.7491 19.0925 10.2222 19.3438 9.75154C19.5857 9.31344 19.9448 8.95143 20.381 8.70615C20.8418 8.46256 21.3582 8.3434 21.8791 8.36043C22.5102 8.34186 23.1296 8.5329 23.6406 8.9037C24.1319 9.26637 24.4789 9.79126 24.6202 10.3854H23.048C22.9437 10.1579 22.7748 9.96608 22.5623 9.83385C22.3422 9.70689 22.092 9.64149 21.838 9.64453C21.6399 9.63802 21.4428 9.67521 21.2607 9.75345C21.0786 9.8317 20.916 9.94908 20.7843 10.0973C20.5056 10.4372 20.3619 10.868 20.381 11.3073C20.3789 11.7356 20.5308 12.1504 20.809 12.4761Z"
                fill="#1A2547"
              />
              <path
                d="M4.28906 23.4073V38.7342C4.28906 38.9853 4.38879 39.226 4.56632 39.4036C4.74384 39.5811 4.98462 39.6808 5.23568 39.6808H32.1442C32.3946 39.6787 32.6341 39.5783 32.8112 39.4012C32.9882 39.2241 33.0886 38.9846 33.0908 38.7342V28.519C33.0908 28.268 32.9911 28.0272 32.8135 27.8497C32.636 27.6721 32.3952 27.5724 32.1442 27.5724H24.8265V23.4073C24.8243 23.1555 24.7227 22.9147 24.5439 22.7374C24.3651 22.5602 24.1234 22.4607 23.8716 22.4607H5.23568C4.98462 22.4607 4.74384 22.5604 4.56632 22.738C4.38879 22.9155 4.28906 23.1563 4.28906 23.4073ZM24.8265 29.4656H31.1976V32.6759H24.8265V29.4656ZM24.8265 34.5691H31.1976V37.7876H24.8265V34.5691ZM17.0725 24.3539H22.9332V27.5724H17.0725V24.3539ZM17.0725 29.4656H22.9332V32.6759H17.0725V29.4656ZM17.0725 34.5691H22.9332V37.7876H17.0725V34.5691ZM6.18229 24.3539H15.1792V27.5724H6.18229V24.3539ZM6.18229 29.4656H15.1792V32.6759H6.18229V29.4656ZM6.18229 34.5691H15.1792V37.7876H6.18229V34.5691Z"
                fill="#1A2547"
              />
            </svg>
            <Box marginRight="10px">
              <Typography className={classes.downloadTemplateTitle}>
                Download template
              </Typography>
              <Typography className={classes.downloadTemplateBody}>
                You can download our template, with an example and use them as a
                starting point for your own file.
              </Typography>
            </Box>
            <GenericButton
              label="Download"
              variant="secondary-black"
              onClick={handleTemplateDownload}
              customStyles={{ width: '160px', fontSize: '16px' }}
            />
          </Box>
          <Box
            width={'100%'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            margin="20px 0"
          >
            <GenericButton
              disabled={disabled}
              label="Save"
              customStyles={{ width: '240px' }}
              onClick={disabled ? () => {} : onSubmit}
            />
          </Box>
        </form>
      </div>
    </div>
  )
}
